import React from "react"
import { Link } from "react-router-dom"
import { Home, Users, Phone, Mail, Star, Shield, Clock } from "lucide-react"

import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle,
} from "../components/ui/navigation-menu"
import HudsonNavigationMenu from "../components/ui/hudson-navigation"

const demoComponents = [
  {
    title: "Services Overview",
    to: "/#services",
    description: "Complete home management and concierge services for discerning homeowners"
  },
  {
    title: "Membership Tiers",
    to: "/#membership",
    description: "Standard, Premium, and Elite membership levels with exclusive benefits"
  },
  {
    title: "Client Portal",
    to: "/portal",
    description: "Secure member access to service requests, schedules, and account management"
  },
  {
    title: "Emergency Support",
    to: "/emergency",
    description: "24/7 rapid response system for urgent home management needs"
  }
]

const NavigationMenuDemoPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-white">
      
      {/* Page Header */}
      <div className="bg-gradient-to-b from-gray-50 to-white py-16 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <h1 className="text-5xl font-bold text-gray-900 mb-6">
            🧭 Navigation Menu Components
          </h1>
          <p className="text-xl text-gray-600 leading-relaxed mb-8">
            Professional dropdown navigation with smooth animations, keyboard accessibility, 
            and responsive design. Perfect for complex site structures and user-friendly navigation.
          </p>
        </div>
      </div>

      {/* Hudson Club Navigation Showcase */}
      <div className="py-16 px-4 bg-white border-b">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">🏠 Hudson Club Navigation</h2>
            <p className="text-gray-600 mb-8">
              Production-ready navigation menu with Hudson Club branding and content
            </p>
          </div>
          
          {/* Navigation Menu Demo */}
          <div className="flex justify-center mb-8">
            <div className="bg-white rounded-lg border p-4 shadow-sm">
              <HudsonNavigationMenu />
            </div>
          </div>

          {/* Features Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            <div className="text-center p-6 bg-gray-50 rounded-lg">
              <Home className="h-8 w-8 text-gray-600 mx-auto mb-3" />
              <h3 className="font-semibold text-gray-900 mb-2">Services Menu</h3>
              <p className="text-sm text-gray-600">Comprehensive service categories with descriptions and icons</p>
            </div>
            
            <div className="text-center p-6 bg-gray-50 rounded-lg">
              <Users className="h-8 w-8 text-gray-600 mx-auto mb-3" />
              <h3 className="font-semibold text-gray-900 mb-2">Membership Tiers</h3>
              <p className="text-sm text-gray-600">Clear hierarchy of membership levels and benefits</p>
            </div>
            
            <div className="text-center p-6 bg-gray-50 rounded-lg">
              <Phone className="h-8 w-8 text-gray-600 mx-auto mb-3" />
              <h3 className="font-semibold text-gray-900 mb-2">Contact Integration</h3>
              <p className="text-sm text-gray-600">Direct contact methods with click-to-call and email</p>
            </div>
          </div>
        </div>
      </div>

      {/* Basic Navigation Example */}
      <div className="py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">📋 Basic Navigation Menu</h2>
            <p className="text-gray-600">
              Flexible navigation component that can be customized for any brand or content structure
            </p>
          </div>
          
          <div className="flex justify-center mb-8">
            <div className="bg-white rounded-lg border p-4 shadow-sm">
              <NavigationMenu>
                <NavigationMenuList>
                  
                  {/* Getting Started Dropdown */}
                  <NavigationMenuItem>
                    <NavigationMenuTrigger>Getting Started</NavigationMenuTrigger>
                    <NavigationMenuContent>
                      <ul className="grid gap-3 p-4 md:w-[400px] lg:w-[500px] lg:grid-cols-[.75fr_1fr]">
                        <li className="row-span-3">
                          <NavigationMenuLink asChild>
                            <Link
                              className="flex h-full w-full select-none flex-col justify-end rounded-md bg-gradient-to-b from-muted/50 to-muted p-6 no-underline outline-none focus:shadow-md"
                              to="/"
                            >
                              <Star className="h-6 w-6 mb-2" />
                              <div className="mb-2 mt-4 text-lg font-medium">
                                Hudson Club
                              </div>
                              <p className="text-sm leading-tight text-muted-foreground">
                                Effortless homeownership through premium management services
                              </p>
                            </Link>
                          </NavigationMenuLink>
                        </li>
                        <DemoListItem to="/#about" title="About Us">
                          Learn about our mission and approach to home management
                        </DemoListItem>
                        <DemoListItem to="/#services" title="Our Services">
                          Comprehensive home management and concierge services
                        </DemoListItem>
                        <DemoListItem to="/membership" title="Discover Home Membership">
                          Join The Hudson Club and experience effortless homeownership
                        </DemoListItem>
                      </ul>
                    </NavigationMenuContent>
                  </NavigationMenuItem>

                  {/* Components Dropdown */}
                  <NavigationMenuItem>
                    <NavigationMenuTrigger>Features</NavigationMenuTrigger>
                    <NavigationMenuContent>
                      <ul className="grid w-[400px] gap-3 p-4 md:w-[500px] md:grid-cols-2 lg:w-[600px]">
                        {demoComponents.map((component) => (
                          <DemoListItem
                            key={component.title}
                            title={component.title}
                            to={component.to}
                          >
                            {component.description}
                          </DemoListItem>
                        ))}
                      </ul>
                    </NavigationMenuContent>
                  </NavigationMenuItem>

                  {/* Single Link */}
                  <NavigationMenuItem>
                    <Link to="/#contact" className={navigationMenuTriggerStyle()}>
                      Contact
                    </Link>
                  </NavigationMenuItem>
                  
                </NavigationMenuList>
              </NavigationMenu>
            </div>
          </div>

        </div>
      </div>

      {/* Technical Features */}
      <div className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">⚡ Technical Features</h2>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
            
            {/* Features List */}
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-3 h-3 rounded-full bg-blue-500 mt-1.5 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Radix UI Foundation</h3>
                  <p className="text-gray-600 text-sm">Built on robust Radix UI primitives for accessibility and keyboard navigation</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-3 h-3 rounded-full bg-green-500 mt-1.5 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Smooth Animations</h3>
                  <p className="text-gray-600 text-sm">Tailwind CSS animations for dropdown transitions and hover effects</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-3 h-3 rounded-full bg-purple-500 mt-1.5 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">React Router Integration</h3>
                  <p className="text-gray-600 text-sm">Seamless integration with React Router for SPA navigation</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-3 h-3 rounded-full bg-yellow-500 mt-1.5 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Responsive Design</h3>
                  <p className="text-gray-600 text-sm">Mobile-first approach with adaptive layouts for all screen sizes</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-3 h-3 rounded-full bg-red-500 mt-1.5 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">TypeScript Support</h3>
                  <p className="text-gray-600 text-sm">Full type safety with comprehensive TypeScript interfaces</p>
                </div>
              </div>
            </div>

            {/* Usage Examples */}
            <div className="bg-gray-900 rounded-lg p-6 text-white">
              <h3 className="text-lg font-semibold mb-4 text-gray-300">Integration Example</h3>
              <pre className="text-sm overflow-x-auto text-green-400">
                <code>{`import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuList,
  NavigationMenuTrigger,
} from '@/components/ui/navigation-menu'

// Hudson Club Navigation
import HudsonNavigationMenu from '@/components/ui/hudson-navigation'

function Header() {
  return (
    <header className="border-b">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          <Logo />
          <HudsonNavigationMenu />
          <CTAButton />
        </div>
      </div>
    </header>
  )
}`}</code>
              </pre>
            </div>
          </div>
        </div>
      </div>

      {/* Perfect for Hudson Club */}
      <div className="py-16 px-4 bg-gradient-to-br from-amber-50 to-orange-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">🏠 Perfect for Hudson Club</h2>
          <p className="text-lg text-gray-700 mb-8">
            This navigation menu elevates your Hudson Club website with professional dropdown menus, 
            smooth animations, and comprehensive content organization that matches your premium brand.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="font-semibold text-gray-900 mb-2">Service Categories</h3>
              <p className="text-gray-600 text-sm">Clear organization of home management and concierge services</p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="font-semibold text-gray-900 mb-2">Membership Levels</h3>
              <p className="text-gray-600 text-sm">Elegant presentation of Standard, Premium, and Elite tiers</p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="font-semibold text-gray-900 mb-2">Contact Integration</h3>
              <p className="text-gray-600 text-sm">Direct access to phone and email with professional presentation</p>
            </div>
          </div>
        </div>
      </div>

    </div>
  )
}

const DemoListItem = React.forwardRef<
  React.ElementRef<typeof Link>,
  React.ComponentPropsWithoutRef<typeof Link> & { title: string }
>(({ className, title, children, ...props }, ref) => {
  return (
    <li>
      <NavigationMenuLink asChild>
        <Link
          ref={ref}
          className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
          {...props}
        >
          <div className="text-sm font-medium leading-none">{title}</div>
          <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
            {children}
          </p>
        </Link>
      </NavigationMenuLink>
    </li>
  )
})
DemoListItem.displayName = "DemoListItem"

export default NavigationMenuDemoPage 